import {StrictMode, Suspense} from "react";
import {createRoot} from "react-dom/client";
import {Provider} from "react-redux";
import {
    createBrowserRouter,
    RouterProvider,
    RouteObject,
    Outlet,
} from "react-router-dom";
import {LoadingOverlay, MantineProvider} from "@mantine/core";
import {ModalsProvider} from "@mantine/modals";
import {Notifications} from "@mantine/notifications";
import {store} from "./app/store";
import theme from "./app/theme";
import {useTranslation, I18nextProvider} from "react-i18next";
import i18n from "./i18n";
import {Intercom} from "./Intercom.tsx";
import {AuthorizationGate} from "./AuthorizationGate.tsx";
import "@fontsource/lato/400.css";
import "@fontsource/lato/700.css";
import "@fontsource/pt-serif/400.css";
import "@fontsource/pt-serif/700.css";
import "@mantine/notifications/styles.css";
import {ErrorBoundary} from "./ErrorBoundary.tsx";
import SessionExpired from "./SessionExpired.tsx";
import {ResponseTypeMigration} from "./ResponseTypeMigration.tsx";
import "./sentry";
import {ContentPage} from "./features/content/ContentPage.tsx";
import {TOPIC_PARTIAL_PATHS, LEGACY_PARTIAL_PATHS} from "./features/content/paths.ts";
import {App as ChatLayout} from "./features/chat/App.tsx";
import {SettingsPage} from "./features/chat/SettingsPage.tsx";
import {RouterError} from "./RouterError.tsx";
import ChatView from "./features/chat/sliceChatComponents/ChatView.tsx";
import {DashboardGuard} from "./features/dashboard/DashboardGuard.tsx";
import {PortalsPage} from "./features/dashboard/PortalsPage.tsx";
import {MyWorkPage} from "./features/dashboard/MyWorkPage.tsx";
import {HistoryPage} from "./features/dashboard/HistoryPage.tsx";
import {ChatSearchHistoryPage} from "./features/dashboard/ChatSearchHistoryPage.tsx";

// --- Define Routes ---

// React Router v6 doesn't support inline regex constraints, so we
// generate explicit routes for each known dokumentType (matching
// DibApp2020's constrained partialPath).
const PARTIAL_TYPES = [
    ...Object.values(TOPIC_PARTIAL_PATHS),
    ...LEGACY_PARTIAL_PATHS,
];

const topicBase = ":topicType/:topicName/:topicId";

const partialRoutes: RouteObject[] = PARTIAL_TYPES.flatMap((dt) => [
    {
        path: `${topicBase}/${dt}/:dokumentName/:dokumentId/utfylling/:collectionName/:collectionId`,
        element: <ContentPage />,
    },
    {
        path: `${topicBase}/${dt}/:dokumentName/:dokumentId`,
        element: <ContentPage />,
    },
]);

// Routes that use the ChatLayout
const chatLayoutRoutes: RouteObject[] = [
    {
        element: <ChatLayout />,
        children: [
            {
                element: <DashboardGuard />,
                children: [
                    {
                        index: true,
                        element: <PortalsPage />,
                    },
                    {
                        path: "my-work",
                        element: <MyWorkPage />,
                    },
                    {
                        path: "history",
                        element: <HistoryPage />,
                    },
                    {
                        path: "chat-search",
                        element: <ChatSearchHistoryPage />,
                    },
                ],
            },
            {
                path: "chat/:threadId",
                element: <ChatView />,
            },
            {
                path: "settings",
                element: <SettingsPage />,
            },
            {
                path: "preview/:topicId",
                element: <ContentPage />,
            },
            ...partialRoutes,
            {
                path: `${topicBase}/utfylling/:collectionName/:collectionId`,
                element: <ContentPage />,
            },
            {
                path: `${topicBase}/segment/:segmentId`,
                element: <ContentPage />,
            },
            {
                path: topicBase,
                element: <ContentPage />,
            },
        ],
    },
];

const LoadingFallback = () => {
    const {t} = useTranslation();
    return <div>{t("common.loadingTranslations")}</div>;
};

const router = createBrowserRouter([
    {
        path: "/",
        element: (
            <Suspense fallback={<LoadingOverlay visible={true} />}>
                <Outlet />
            </Suspense>
        ),
        errorElement: <RouterError />,
        children: chatLayoutRoutes,
    },
]);

createRoot(document.getElementById("root")!).render(
    <StrictMode>
        <Provider store={store}>
            <MantineProvider theme={theme}>
                <Notifications />
                <ModalsProvider>
                    <I18nextProvider i18n={i18n}>
                        <ErrorBoundary fallback={<LoadingFallback />}>
                            <Suspense fallback={<LoadingFallback />}>
                                <SessionExpired />
                                <ResponseTypeMigration />
                                <AuthorizationGate>
                                    <RouterProvider router={router} />
                                </AuthorizationGate>
                                <Intercom />
                            </Suspense>
                        </ErrorBoundary>
                    </I18nextProvider>
                </ModalsProvider>
            </MantineProvider>
        </Provider>
    </StrictMode>
);
